/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { useServiceContext } from '../../../../root'
import TableDateTimeCell from '../../../../components/shared/TableDateTimeCell'
import TextLinkButton from '../../../../components/shared/TextLinkButton'
import { TaskStatus, WorkflowStepType, FsiType, WorkflowType } from '../../types'
import TaskMenu from '../../TaskMenu'
import { validateFileFactory } from '../../../../services/factory/validateFileFactory'
import { WorkflowDetailsModal } from '../../../../components/modals/WorkflowDetailsModal'
import ParticipatingFsisModal from '../../../../components/modals/ParticipatingFsisModal'
import { ReadCustomJustificationModal } from '../../../../components/modals/ReadCustomJustificationModal'
import { useModal } from '../../../../contexts/ModalContext'
import { useNavigate } from 'react-router'
import { useNavigateWithParams } from '../../../../hooks/useNavigateWithParams'
import { checkAccessRedirectionForQC } from '../../../../hooks/useCheckDocAccessInQC'
import { toast } from '../../../../services/toast'
import { getFileDetail } from '../../../../services/api/files'
import { ApiError } from '../../../../services/api/base'
import { formatUserDateTime } from '~/utils/dateFormatting'

interface TaskDetailsTableProps {
  task: any
  fileFullPath?: string
  onClose?: () => void
}

const TaskDetailsTable = ({ task, fileFullPath = '', onClose }: TaskDetailsTableProps) => {
  const context = useServiceContext()
  const navigate = useNavigate() // Standard navigate for checkAccessRedirectionForQC
  const navigateWithParams = useNavigateWithParams() // For other navigation with params
  const translate = context.service.filter.translate
  const excerpt = (text: string, skipTrim?: boolean, length = 30) => {
    if (!text) return ''
    return text.length > length ? text.substring(0, length) + '...' : text
  }
  const [isEosAcknowledge, setIsEosAcknowledge] = useState(false)
  const [isEosDownload, setIsEosDownload] = useState(false)
  const [taskFile, setTaskFile] = useState<any>(null)
  const { showModal } = useModal()

  const checkAccessQC = (task: any) => {
    const currentLoggedInUserId = context.service.auth.getUserId()
    const userRole = context.service.auth.getRole()
    const currentTaskAssigneeIds = [task?.userId]
    const wfId = task?.workflowId
    const wfInitiatorId = task?.initiatorId
    const wfStatus = task?.workflowStatus

    checkAccessRedirectionForQC({
      wfStatus,
      currentLoggedInUserId,
      currentTaskAssigneeIds,
      wfInitiatorId,
      wfId,
      workflowInfo: task?.workflow,
      isWorkflow: false,
      task,
      taskIdWhenWf: null,
      userRole,
      api: context.api,
      service: context.service,
      navigate
    })
  }

  useEffect(() => {
    if (task && task.id) {
      setIsEosDownload(task.workflowStepType === WorkflowStepType.DOWNLOAD_EOS)
      setIsEosAcknowledge(task.workflowStepType === WorkflowStepType.ACKNOWLEDGE_EOS)

      if (task.files && task.files.length === 1) {
        const file = task.files[0]
        setTaskFile(file.draftFsi || file.targetFsi || file.sourceFsi || null)
      }
    }
  }, [task])

  const handleDocumentClick = async (fsi: any) => {
    if (task?.workflowType === WorkflowType.QC_REVIEW) {
      checkAccessQC(task)
      onClose?.()
    } else {
      const fileId = fsi.id
      const versionId = fsi.versionId
      const minorVersionId = fsi.minorVersionId

      try {
        // Call file detail API to validate access before navigating
        await getFileDetail(fileId)

        // Access granted - navigate and close modal
        navigateWithParams(
          `/show-file/${fileId}?action=taskComplete&taskId=${task.id}&versionId=${versionId || -1}&minorVersionId=${minorVersionId || 0}`
        )
        onClose?.()
      } catch (error) {
        // Access denied or API error - show toast with API message and keep modal open
        if (error instanceof ApiError && error.body && typeof error.body === 'object') {
          const errorMessage = (error.body as any)?.message || translate('FILE_ACCESS_RIGHTS_ERROR')
          toast.error(errorMessage)
        } else {
          toast.error(translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
        }
        // Don't call onClose() - keep modal open
      }
    }
  }

  const handleMultipleDocsClick = async () => {
    if (task?.workflowType === WorkflowType.QC_REVIEW) {
      checkAccessQC(task)
      onClose?.()
    } else {
      // Validate access to first file before showing modal
      if (task?.files && task.files.length > 0) {
        const firstFile = task.files[0]
        const fsi = firstFile.draftFsi || firstFile.targetFsi || firstFile.sourceFsi

        if (fsi?.id) {
          try {
            // Call file detail API to validate access
            await getFileDetail(fsi.id)

            // Access granted - show modal and keep parent open
            showModal(<ParticipatingFsisModal wfId={task.workflowId} show={true} onClose={() => {}} />, {
              keepParentModalsOpen: true
            })
            // Don't call onClose() - keepParentModalsOpen keeps TaskDetailsModal open
          } catch (error) {
            // Access denied or API error - show toast with API message and keep modal open
            if (error instanceof ApiError && error.body && typeof error.body === 'object') {
              const errorMessage = (error.body as any)?.message || translate('FILE_ACCESS_RIGHTS_ERROR')
              toast.error(errorMessage)
            } else {
              toast.error(translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
            }
            // Don't call onClose() - keep modal open
          }
        } else {
          // No file ID, show modal anyway and keep parent open
          showModal(<ParticipatingFsisModal wfId={task.workflowId} show={true} onClose={() => {}} />, {
            keepParentModalsOpen: true
          })
          // Don't call onClose() - keepParentModalsOpen keeps TaskDetailsModal open
        }
      }
    }
  }

  const handleWorkflowDetailsClick = () => {
    showModal(<WorkflowDetailsModal wfId={task.workflow?.id} />)
  }

  const handleCustomJustificationClick = () => {
    showModal(<ReadCustomJustificationModal workflow={{ id: task.workflow.id }} />, { keepParentModalsOpen: true })
    onClose?.()
  }

  const handleEosDownload = () => {
    if (task.eosDownloadMediaId) {
      const validateFile = validateFileFactory()
      validateFile.downloadOriginal(task.eosDownloadMediaId)
    }
    onClose?.()
  }

  if (!task) return null

  return (
    <table className="task-detail-table">
      <tbody>
        <tr>
          <td className="name pt0">{translate('TYPE')}</td>
          <td className="value pt0">{task.name}</td>
        </tr>
        <tr>
          <td className="name pt0">{translate('DESCRIPTION')}</td>
          <td className="value pt0">{task.workflow?.description || task.workflowDescription}</td>
        </tr>

        {(isEosDownload || isEosAcknowledge) && (
          <tr>
            <td className="name va-top">{translate('EMTF_MD_STUDY')}</td>
            <td className="value word-break">{task.studyName || translate('NOT_AVAILABLE')}</td>
          </tr>
        )}

        {(isEosDownload || isEosAcknowledge) && (
          <tr>
            <td className="name va-top">{translate('EMTF_MD_SITE')}</td>
            <td className="value word-break">{task.siteName || translate('NOT_AVAILABLE')}</td>
          </tr>
        )}

        {task.workflow?.justificationText && (
          <tr>
            <td className="name va-top">{translate('JUSTIFICATION')}</td>
            {!task.workflow.customJustification && (
              <td className="value word-break">{task.workflow.justificationText}</td>
            )}
            {task.workflow.customJustification && (
              <td className="value word-break">
                <TextLinkButton onClick={handleCustomJustificationClick} title={task.workflow.justificationText}>
                  {excerpt(task.workflow.justificationText, false, 30)}
                </TextLinkButton>
              </td>
            )}
          </tr>
        )}

        {task.reasonForReject && (
          <tr>
            <td className="name va-top">{translate('TASK_REJECTION_REASON_LABEL')}</td>
            <td className="value word-break">{task.reasonForReject}</td>
          </tr>
        )}

        <tr>
          <td className="name va-top">
            {translate(
              isEosAcknowledge ? 'EOS_MEDIA_ACKNOWLEDGEMENT_FORM' : isEosDownload ? 'EOS_MEDIA' : 'DOCUMENT(S)'
            )}
          </td>
          {isEosDownload ? (
            <td className="value word-break">
              {task.eosDownloadMediaName && task.eosDownloadMediaId && task.status === TaskStatus.APPROVED && (
                <TextLinkButton action={handleEosDownload}>{task.eosDownloadMediaName}</TextLinkButton>
              )}
              {task.eosDownloadMediaName && task.status !== TaskStatus.APPROVED && (
                <span>{task.eosDownloadMediaName}</span>
              )}
              {!task.eosDownloadMediaName && translate('NOT_AVAILABLE')}
            </td>
          ) : (
            <td className="value word-break">
              {task.files && task.files.length > 1 && (
                <TextLinkButton action={handleMultipleDocsClick}>{translate('WORKFLOW_MULTIPLE_DOCS')}</TextLinkButton>
              )}

              {(!task.files || task.files.length === 0) && (
                <span className="whitespaceprewrap" title={fileFullPath + task.filename}>
                  {task.filename || translate('NOT_AVAILABLE')}
                </span>
              )}

              {task.files && task.files.length === 1 && taskFile && (
                <span title={fileFullPath + taskFile.name}>
                  {taskFile.type === FsiType.PLACEHOLDER ? (
                    taskFile.name
                  ) : (
                    <TextLinkButton action={() => handleDocumentClick(taskFile)} className="whitespaceprewrap">
                      {taskFile.name}
                    </TextLinkButton>
                  )}
                </span>
              )}
            </td>
          )}
        </tr>

        <tr>
          <td className="name va-top">{translate('WORKFLOW_CHANGE_REQUEST_NUMBER')}</td>
          <td className="value va-top">
            <TextLinkButton action={handleWorkflowDetailsClick}>
              {task.workflowFormattedId || task.workflow?.formattedId}
            </TextLinkButton>
          </td>
        </tr>

        <tr>
          <td className="name">{translate('INITIATOR')}</td>
          <td className="value">
            <a href={'mailto:' + task.initiatorEmail}>{task.initiatorName}</a>
          </td>
        </tr>

        <tr>
          <td className="name">{translate('ASSIGNED_TO')}</td>
          <td className="value">
            <a href={'mailto:' + task.assigneeEmail}>{task.userName}</a>
          </td>
        </tr>

        <tr>
          <td className="name">{translate('START_DATE')}</td>
          <td className="value">
            <span>{formatUserDateTime(task.startDate)}</span>{' '}
          </td>
        </tr>

        <tr>
          <td className="name">{translate('FINISH_DATE')}</td>
          <td className="value">
            <span>{task.endDate ? formatUserDateTime(task.endDate) : translate('TASKS_IN_PROCESS')}</span>
          </td>
        </tr>

        {task.status === TaskStatus.OPEN && (
          <tr>
            <td className="name">{translate('ACTIONS')}</td>
            <td className="actions value">
              <TaskMenu task={task} hideDetails={true} onClose={onClose} />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

export default TaskDetailsTable
